import React from "react";
import {
  birthDateOverrideString,
  genderOverrideString,
  multipleBirthBooleanOverrideString,
  genericOverrideURL,
  getValueTypeByAttribute,
  overriddenByString,
  overrideReasonString,
  overrideTimestampString,
  dobOverriddenByString,
  dobOverrideReasonString,
  dobOverrideTimestampString,
  genderOverriddenByString,
  genderOverrideReasonString,
  genderOverrideTimestampString,
  multipleBirthOverriddenByString,
  multipleBirthOverrideReasonString,
  multipleBirthOverrideTimestampString
} from "../GoldenRecordExclusion/Utils";

export const createOverrideAuditExtension = (auditDetails, attribute) => {
  const { username, incidentNumber, moreInfo } = auditDetails;
  const valueType = getValueTypeByAttribute();

  // Pick correct URLs based on attribute
  let overriddenByUrl = overriddenByString;
  let overrideReasonUrl = overrideReasonString;
  let overrideTimestampUrl = overrideTimestampString;

  if (attribute === "birthDate") {
    overriddenByUrl = dobOverriddenByString;
    overrideReasonUrl = dobOverrideReasonString;
    overrideTimestampUrl = dobOverrideTimestampString;
  } else if (attribute === "gender") {
    overriddenByUrl = genderOverriddenByString;
    overrideReasonUrl = genderOverrideReasonString;
    overrideTimestampUrl = genderOverrideTimestampString;
  } else if (attribute === "multipleBirthBoolean") {
    overriddenByUrl = multipleBirthOverriddenByString;
    overrideReasonUrl = multipleBirthOverrideReasonString;
    overrideTimestampUrl = multipleBirthOverrideTimestampString;
  }

  return [
    {
      url: overriddenByUrl,
      [valueType]: username
    },
    {
      url: overrideReasonUrl,
      [valueType]: `${incidentNumber} - ${moreInfo}`
    },
    {
      url: overrideTimestampUrl,
      [valueType]: getFormattedTimestamp()
    }
  ];
};

const overrideUrl = attribute => {
  switch (attribute) {
    case "birthDate":
      return birthDateOverrideString;
      break;
    case "gender":
      return genderOverrideString;
      break;
    case "multipleBirthBoolean":
      return multipleBirthBooleanOverrideString;
      break;
    case "userName":
      return overriddenByString;
      break;
    case "reason":
      return overrideReasonString;
      break;
    case "timestamp":
      return overrideTimestampString;
      break;
    default:
      return genericOverrideURL;
      break;
  }
};

function getFormattedTimestamp() {
  // Get the ISO string, remove milliseconds and 'Z', then add '+00:00'
  return new Date().toISOString().replace(/\.\d{3}Z$/, "+00:00");
}

const PrepareOverrides = (record, attributeOverrides, auditDetails) => {
  const patientRecord = JSON.parse(JSON.stringify(record));
  const overrides = { ...attributeOverrides };

  const requestPayload = {
    resourceType: patientRecord.resourceType,
    id: patientRecord.id,
    meta: {
      ...patientRecord.meta,
      lastUpdated: ""
    }
  };

  // Helper function to create an override extension for a given attribute.
  const createOverrideExtension = detailMap => {
    const attributeName = detailMap.originalFieldName;
    const valueType = getValueTypeByAttribute(attributeName);

    const overrideExtension = { url: overrideUrl(attributeName) };
    overrideExtension[valueType] = detailMap.originalFieldDataCopy;

    return overrideExtension;
  };

  // Helper function to push an item into an array in the request payload.
  // Use the key value if it exists, otherwise initialize it as an empty array.
  const pushObject = (object, key, item) => {
    object[key] = object[key] || new Array();
    object[key].push(item);
  };

  // Adding all previous overrides to the request payload by looping through all keys in the patient record
  Object.keys(patientRecord).forEach(key => {
    if (patientRecord[key] instanceof Array) {
      if (key === "extension") {
        // multipleBirth, gender and birthDate are all single valued and their overrides exist in the top level extension
        patientRecord["extension"]
          .filter(e => e.hasOwnProperty("url"))
          .forEach(e => {
            // Collect all audit extensions for birthDate
            const birthDateAuditUrls = [
              birthDateOverrideString,
              dobOverriddenByString,
              dobOverrideReasonString,
              dobOverrideTimestampString
            ];
            if (birthDateAuditUrls.includes(e["url"])) {
              pushObject(requestPayload, "extension", e);
              requestPayload["birthDate"] = patientRecord["birthDate"];
            }
            // Collect all audit extensions for gender
            const genderAuditUrls = [
              genderOverrideString,
              genderOverriddenByString,
              genderOverrideReasonString,
              genderOverrideTimestampString
            ];
            if (genderAuditUrls.includes(e["url"])) {
              pushObject(requestPayload, "extension", e);
              requestPayload["gender"] = patientRecord["gender"];
            }
            // Collect all audit extensions for multipleBirthBoolean
            const multipleBirthAuditUrls = [
              multipleBirthBooleanOverrideString,
              multipleBirthOverriddenByString,
              multipleBirthOverrideReasonString,
              multipleBirthOverrideTimestampString
            ];
            if (multipleBirthAuditUrls.includes(e["url"])) {
              pushObject(requestPayload, "extension", e);
              requestPayload["multipleBirthBoolean"] =
                patientRecord["multipleBirthBoolean"];
            }
          });
      } else {
        // all other attributes are multi-valued and their overrides exist in the extension of each item
        patientRecord[key].forEach(item => {
          item.hasOwnProperty("extension") &&
            item["extension"].some(e => genericOverrideURL.includes(e.url)) &&
            pushObject(requestPayload, key, item);
        });
      }
    }
  });

  // Adding all new overrides to the request payload by looping through all keys in the attributeOverrides object
  Object.keys(overrides).forEach(key => {
    overrides[key].forEach(detailMap => {
      const attributeName = detailMap.originalFieldName;
      const action = detailMap["Mark as"];
      const overrideExtension = createOverrideExtension(detailMap);
      const overrideAuditExtension = createOverrideAuditExtension(
        auditDetails,
        attributeName
      );

      if (
        ["birthDate", "gender", "multipleBirthBoolean"].includes(attributeName)
      ) {
        // Adding the created override extension to the top level extension and adding the attribute value to the request payload.
        pushObject(requestPayload, "extension", overrideExtension);
        //push override audit extensions as well inside extension
        overrideAuditExtension.forEach(ext => {
          requestPayload["extension"].push(ext);
        });
        requestPayload[attributeName] = detailMap.originalFieldData;
      } else {
        const attributeObject = detailMap.originalFieldData;

        // Adding the created override extension to the attribute object and adding the attribute object to the request
        // payload for each action type. Inactive types need to have their use set to "old" and invalid types do not have
        // new values so an empty object is created.
        if (action === "Replace") {
          pushObject(attributeObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach(ext => {
            attributeObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, attributeObject);
        }
        if (action === "Inactive") {
          attributeObject["use"] = "old";
          pushObject(attributeObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach(ext => {
            attributeObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, attributeObject);
        }
        if (action === "Invalid") {
          const invalidObject = {};
          invalidObject["extension"] = new Array();
          pushObject(invalidObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach(ext => {
            invalidObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, invalidObject);
        }
      }
    });
  });

  return requestPayload;
};

export default PrepareOverrides;
